import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tab";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import ContactPatient from "./contact-patient";

const PatientsList = lazy(() => import("./patients-list"));
const PatientWaitingList = lazy(() => import("./patient-waiting-list"));

function PatientTabContent() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("patients");
  const [showModal, setShowModal] = useState<boolean>(false);
  const patientsRefetchRef = useRef<(() => void) | null>(null);
  const waitingRefetchRef = useRef<(() => void) | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<{
    pid: string;
    name: string;
    visitTime: string;
    isPending: boolean;
  } | null>(null);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === "patients") {
      patientsRefetchRef.current?.();
    }
    if (value === "waiting") {
      waitingRefetchRef.current?.();
    }
  };
  const selectPatient = useCallback(
    (data: {
      pid: string;
      name: string;
      visitTime: string;
      isPending: boolean;
    }) => {
      setSelectedPatient(data);
      setShowModal(true);
    },
    []
  );

  const tabClassNameOverWrite = useMemo(() => {
    return {
      tabs: "flex flex-col border-t pb-0 gap-0 h-full",
      tabsList: "bg-transparent border-none p-0 h-10",
      tabsTrigger:
        "w-20 h-10 text-foreground rounded-md rounded-b-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary",
    };
  }, []);

  return (
    <Tabs
      className={tabClassNameOverWrite.tabs}
      dir={i18n.dir()}
      defaultValue="patients"
      value={activeTab}
      onValueChange={handleTabChange}
    >
      {
        <TabsList className={tabClassNameOverWrite.tabsList} animated={false}>
          <TabsTrigger
            value="patients"
            className={`${tabClassNameOverWrite.tabsTrigger} relative`}
          >
            {t("patients")}
            {activeTab === "patients" && (
              <motion.div
                layoutId="active-tab-border"
                className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </TabsTrigger>
          <TabsTrigger
            value="waiting"
            className={`${tabClassNameOverWrite.tabsTrigger} relative`}
          >
            {t("waiting")}
            {activeTab === "waiting" && (
              <motion.div
                layoutId="active-tab-border"
                className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </TabsTrigger>
        </TabsList>
      }
      <TabsContent
        value="patients"
        forceMount
        className={cn(
          "w-full border-t h-full",
          activeTab !== "patients" && "hidden"
        )}
      >
        <Suspense fallback={null}>
          <PatientsList
            selectPatient={selectPatient}
            onRefetchReady={(fn) => (patientsRefetchRef.current = fn)}
          />
        </Suspense>
      </TabsContent>
      {
        <TabsContent
          value="waiting"
          forceMount
          className={cn(
            "w-full border-t h-full",
            activeTab !== "waiting" && "hidden"
          )}
        >
          <Suspense fallback={null}>
            <PatientWaitingList
              selectPatient={selectPatient}
              onRefetchReady={(fn) => (waitingRefetchRef.current = fn)}
            />
          </Suspense>
        </TabsContent>
      }
      <ContactPatient
        selectedPatient={selectedPatient}
        showModal={showModal}
        setShowModal={setShowModal}
      />
    </Tabs>
  );
}
export default memo(PatientTabContent);
